// Small display helpers shared across the WHCA shell.

/** Up to two uppercase initials from a name (falls back to "?"). */
export function getInitials(name?: string | null): string {
  if (!name) return '?'
  return (
    name
      .split(' ')
      .filter(Boolean)
      .map((w) => w[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) || '?'
  )
}

/**
 * Compact relative-ish time for the rail rows: "6:42a" today, weekday this
 * week, otherwise a short date.
 */
export function shortTime(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  const now = new Date()
  const sameDay =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()

  if (sameDay) {
    let h = date.getHours()
    const m = date.getMinutes().toString().padStart(2, '0')
    const suffix = h >= 12 ? 'p' : 'a'
    h = h % 12
    if (h === 0) h = 12
    return `${h}:${m}${suffix}`
  }

  const diffDays = Math.floor((now.getTime() - date.getTime()) / 86400000)
  if (diffDays < 7) {
    return date.toLocaleDateString([], { weekday: 'short' })
  }
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' })
}

/** Clock time like "2:05 PM" for message bubbles. */
export function clockTime(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

/**
 * Insert a template body into a message field. Replaces an empty field, and
 * appends (on a new line) when the user has already typed something — picking a
 * template must never silently wipe work in progress.
 */
export function insertTemplateBody(current: string, body: string): string {
  return current.trim() ? `${current.trimEnd()}\n${body}` : body
}

// Every broadcast text starts with one of these. The server adds them; these are
// only for previews.
const SENDER_PREFIX = 'WHCA Notify: '
const URGENT_SENDER_PREFIX = 'WHCA Notify URGENT: '

export function senderPrefix(urgent: boolean): string {
  return urgent ? URGENT_SENDER_PREFIX : SENDER_PREFIX
}

/** A message as a broadcast recipient will receive it. */
export function withSenderPrefix(body: string, urgent: boolean): string {
  const lower = body.toLowerCase()
  const existing = [URGENT_SENDER_PREFIX, SENDER_PREFIX].find((p) =>
    lower.startsWith(p.toLowerCase()),
  )
  return senderPrefix(urgent) + (existing ? body.slice(existing.length) : body)
}

/**
 * Template placeholders still left in a message, like "[site]". A text goes out
 * as typed, so an unfilled one reaches every phone looking like a mistake at the
 * moment people most need to trust it.
 */
export function unfilledPlaceholders(text: string): string[] {
  return Array.from(new Set(text.match(/\[[^\]\n]{1,40}\]/g) ?? []))
}

/** "3 people · 2 groups + 1 individual" style recipient summary. */
export function recipientLine(total: number, groups: number, individuals: number): string {
  const parts: string[] = []
  if (groups) parts.push(`${groups} group${groups > 1 ? 's' : ''}`)
  if (individuals) parts.push(`${individuals} individual${individuals > 1 ? 's' : ''}`)
  const base = `${total} ${total === 1 ? 'person' : 'people'}`
  return parts.length ? `${base} · ${parts.join(' + ')}` : base
}
